/**
 * Route guards for the Patient Master surface
 * (ADORACARE-PATIENT-MASTER-B2). Mirrors `RequireLeadsWorkspace`: the
 * pure decisions live in `patient-master-view.ts` and key off the
 * SERVER-computed `/api/dashboard/me` signals —
 *
 *   - `RequirePatientMaster`      → `me.patientMasterEnabled`
 *     (search / create / record routes; receptionist+ create audience)
 *   - `RequirePatientMasterMerge` → `me.patientMasterMergeEnabled`
 *     (merge queue admin; coc / "branch_manager and above")
 *
 * Each signal is present ONLY when the backend flag
 * PATIENT_MASTER_V1_ENABLED is on AND the role is in that audience.
 * Absent (flag off, doctor, etc.) → redirect to the caller's own
 * homePath, so with the flag off nothing new is reachable and no UI
 * changes. Backend route auth remains the real security boundary;
 * these guards are UX cleanliness only.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import {
  decidePatientMasterAccess,
  decidePatientMasterMergeAccess,
  type PatientMasterAccessDecision,
} from '@/dashboard/patients/patient-master-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequirePatientMasterViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

function renderDecision(
  decision: PatientMasterAccessDecision,
  children: React.ReactNode,
): JSX.Element | null {
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

/** Pure presentational view — testable via react-dom/server. */
export function RequirePatientMasterView({
  loading,
  error,
  me,
  children,
}: RequirePatientMasterViewProps): JSX.Element | null {
  return renderDecision(
    decidePatientMasterAccess({ loading, error, me }),
    children,
  );
}

/** Pure presentational view for the merge-admin gate. */
export function RequirePatientMasterMergeView({
  loading,
  error,
  me,
  children,
}: RequirePatientMasterViewProps): JSX.Element | null {
  return renderDecision(
    decidePatientMasterMergeAccess({ loading, error, me }),
    children,
  );
}

export default function RequirePatientMaster(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequirePatientMasterView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}

export function RequirePatientMasterMerge(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequirePatientMasterMergeView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
